/**
 * Date Range Computation Utilities
 * This is the SINGLE SOURCE OF TRUTH for date range validation in the frontend.
 * Must match backend logic exactly.
 */

/**
 * Compute the maximum allowed end date based on start date and cycle type.
 * @param {string} startDateStr - Start date in YYYY-MM-DD format
 * @param {string} cycleType - Cycle type (Weekly, 15-Day, 30-Day, etc.)
 * @returns {string} Maximum allowed end date in YYYY-MM-DD format
 */
export function computeAllowedEndDate(startDateStr, cycleType) {
  const startDate = new Date(startDateStr + 'T00:00:00');
  let endDate;

  switch (cycleType) {
    case 'Weekly':
      // Exact 7-day window: end = start + 6 days
      endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 6);
      break;
      
    case '15-Day':
      // end = start + 14 days
      endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 14);
      break;
      
    case '30-Day':
      // end = start + 29 days
      endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 29);
      break;
      
    case '35-Day':
      // end = start + 34 days
      endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 34);
      break;
      
    case '45-Day':
    case '30-45 Day Strategic':
      // end = start + 44 days
      endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 44);
      break;
      
    case 'Monthly':
      // End must be within same calendar month (last day of month)
      endDate = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0);
      break;
      
    case '6-Month':
      // End within start + 6 calendar months - 1 day
      endDate = new Date(startDate);
      endDate.setMonth(startDate.getMonth() + 6);
      endDate.setDate(endDate.getDate() - 1);
      break;
      
    case '1-Year':
      // End within start + 1 calendar year - 1 day
      endDate = new Date(startDate);
      endDate.setFullYear(startDate.getFullYear() + 1);
      endDate.setDate(endDate.getDate() - 1);
      break;
      
    default:
      // Default: allow up to 1 year
      endDate = new Date(startDate);
      endDate.setFullYear(startDate.getFullYear() + 1);
      endDate.setDate(endDate.getDate() - 1);
  }

  return endDate.toISOString().split('T')[0];
}

/**
 * Get the auto-filled end date for fixed-duration cycles.
 * For fixed cycles (Weekly, 15-Day, 30-Day, 35-Day, 45-Day), this returns the exact end date.
 * For variable cycles (Monthly, 6-Month, 1-Year), returns null (user must select).
 * @param {string} startDateStr - Start date in YYYY-MM-DD format
 * @param {string} cycleType - Cycle type
 * @returns {string|null} Auto-filled end date or null if user must select
 */
export function getAutoFilledEndDate(startDateStr, cycleType) {
  const fixedCycles = ['Weekly', '15-Day', '30-Day', '35-Day', '45-Day', '30-45 Day Strategic'];
  
  if (fixedCycles.includes(cycleType)) {
    return computeAllowedEndDate(startDateStr, cycleType);
  }
  
  // For Monthly, 6-Month, 1-Year: user selects within allowed range
  return null;
}

/**
 * Check if the cycle type has a fixed duration (end date is auto-computed).
 * @param {string} cycleType - Cycle type
 * @returns {boolean} True if end date should be auto-filled
 */
export function isFixedDurationCycle(cycleType) {
  const fixedCycles = ['Weekly', '15-Day', '30-Day', '35-Day', '45-Day', '30-45 Day Strategic'];
  return fixedCycles.includes(cycleType);
}

/**
 * Validate a date range for a given cycle type.
 * @param {string} startDateStr - Start date in YYYY-MM-DD format
 * @param {string} endDateStr - End date in YYYY-MM-DD format
 * @param {string} cycleType - Cycle type
 * @returns {{isValid: boolean, error: string}} Validation result
 */
export function validateDateRange(startDateStr, endDateStr, cycleType) {
  const startDate = new Date(startDateStr + 'T00:00:00');
  const endDate = new Date(endDateStr + 'T00:00:00');
  
  // Rule 1: end_date cannot be before start_date
  if (endDate < startDate) {
    return { isValid: false, error: 'End date cannot be before start date' };
  }
  
  // Rule 2: end_date cannot exceed allowed range
  const maxEndDateStr = computeAllowedEndDate(startDateStr, cycleType);
  const maxEndDate = new Date(maxEndDateStr + 'T00:00:00');
  
  if (endDate > maxEndDate) {
    return { 
      isValid: false, 
      error: `End date exceeds allowed range for ${cycleType}. Maximum allowed: ${maxEndDateStr}` 
    };
  }
  
  return { isValid: true, error: '' };
}
